// The words the read surface puts on what the API returns. Pure functions, so the
// phrasing is testable without a DOM: what a domain tier means in plain English, what
// shape of question a pattern key stands for, and how old an item is.
//
// The tier descriptions are the design's own, copied from packages/prompts/assign_tier/v1.md
// so the user reads the same definition the model was given. H2's is stated as the tier
// (an item about a named employee), never as a quality of a person.

import { dayMonthTime, dayMonthYear } from './day.js';

/** rails.domain_tier, as classify/classes.ts DOMAIN_TIERS spells it. */
export type DomainTier = 'L1' | 'L2' | 'L3' | 'F1' | 'F2' | 'F3' | 'H1' | 'H2' | 'C1' | 'C2' | 'M1' | 'O1' | 'I1';

export interface TierLabel {
  /** The domain the tier belongs to: Legal, Finance, HR, Commercial, Marketing, Ops. */
  domain: string;
  /** What the tier covers, in the design's own words. */
  gist: string;
}

const TIERS: Record<DomainTier, TierLabel> = {
  L1: { domain: 'Legal', gist: 'Client and supplier contracts, NDAs, rights agreements' },
  L2: { domain: 'Legal', gist: 'Corporate: share sales, investment, M&A, board matters' },
  L3: { domain: 'Legal', gist: 'Disputes with or about an employee; regulatory investigations' },
  F1: { domain: 'Finance', gist: 'Client invoicing, supplier bills, receipts' },
  F2: { domain: 'Finance', gist: 'Payroll, compensation, benefits' },
  F3: { domain: 'Finance', gist: 'Corporate finance, investors, fundraising, valuations' },
  H1: { domain: 'HR', gist: 'People operations: hiring, policies, onboarding' },
  H2: { domain: 'HR', gist: 'An item about a named employee' },
  C1: { domain: 'Commercial', gist: 'Client and prospect conversations, pipeline, meeting notes' },
  C2: { domain: 'Commercial', gist: 'Pricing, deal terms, margins, rev-share models' },
  M1: { domain: 'Marketing', gist: 'Brand, PR, events, content' },
  O1: { domain: 'Ops', gist: 'Delivery, MCR, incidents, encoders, supplier ops' },
  I1: { domain: 'Industry', gist: 'Trade press, regulation, competitor and market news' },
};

/** Every tier, in the order the design lists them: for the "somewhere else" chooser (B66). */
export const ALL_TIERS: readonly DomainTier[] = Object.keys(TIERS) as DomainTier[];

/** The tier's label, or null when the API sends a tier this build does not know. */
export function tierLabel(tier: string | null): TierLabel | null {
  return tier === null ? null : (TIERS[tier as DomainTier] ?? null);
}

/** "Commercial C1" for a known tier; the code alone for one this build does not know. */
export function tierName(tier: string): string {
  const label = tierLabel(tier);
  return label ? `${label.domain} ${tier}` : tier;
}

/**
 * The counterparty's address domain a pattern key carries (classify/pass.ts
 * privacyFlagPatternKey and personalFlagPatternKey write it as '<kind>:<domain>' and
 * 'personal:<domain>'). Null when there is no key, or when the pass found no outside
 * address and wrote 'none' — never a guess.
 */
export function patternDomain(patternKey: string | null): string | null {
  if (patternKey === null) return null;
  const at = patternKey.indexOf(':');
  if (at < 0) return null;
  const domain = patternKey.slice(at + 1).trim();
  return domain === '' || domain === 'none' ? null : domain;
}

/** "mail from acme.com", or "this correspondent" when the key names no domain. */
export function patternSubtitle(patternKey: string | null): string {
  const domain = patternDomain(patternKey);
  return domain === null ? 'mail like this one' : `mail from ${domain}`;
}

/**
 * What answering this question disposes of (B47, migration 29). The count comes from the
 * API's patternCount, which is the open flags of the pattern including this one, so a
 * count of one means the question is only about this message.
 */
export function settlesLine(patternCount: number): string {
  if (patternCount <= 1) return 'This question is only about this message.';
  const others = patternCount - 1;
  return `Answering settles ${patternCount.toLocaleString('en-GB')} questions of the same shape — this one and ${others.toLocaleString('en-GB')} other${others === 1 ? '' : 's'}.`;
}

/** "1 question" / "788 questions", grouped with thousands separators. */
export function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;
}

export type When = { kind: 'relative'; text: string } | { kind: 'absolute'; iso: string };

/**
 * How old an item is. Under a week it reads as an age, which needs no time zone and so
 * can be tested; older than that the caller renders the date itself in the browser's own
 * zone. An unparseable timestamp reads as its own text.
 *
 * A clock that disagrees by up to a minute still reads 'just now'; a timestamp genuinely
 * in the future reads as its date (B85). A calendar event is dated when the meeting
 * starts, so future signals are ordinary here, and calling next April 'just now' was how
 * fifty untitled meetings came to look like fifty things that had only just happened. The
 * minute of tolerance is what the old rule was actually for, and it is kept.
 */
export function formatWhen(iso: string, now: number = Date.now()): When {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return { kind: 'absolute', iso };
  const ms = now - t;
  if (ms < -60_000) return { kind: 'absolute', iso };
  if (ms < 60_000) return { kind: 'relative', text: 'just now' };
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return { kind: 'relative', text: `${minutes} min ago` };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { kind: 'relative', text: `${hours} hour${hours === 1 ? '' : 's'} ago` };
  const days = Math.floor(hours / 24);
  if (days < 7) return { kind: 'relative', text: `${days} day${days === 1 ? '' : 's'} ago` };
  return { kind: 'absolute', iso };
}

/** The date of an older item, in the browser's own zone: "9 Sep 2026". */
export function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return dayMonthYear(new Date(t));
}

/**
 * What the list says about an item the pipeline has not read yet. D147's owner-first
 * visibility is the point of the line: the item is here, in full, for its owner, before
 * anything has classified it and before anybody else can see it.
 */
export function statusLine(status: 'pending' | 'classified' | 'failed', tier: string | null, personalArea: string | null = null): string {
  // B70: a personal store files by the owner's own areas, not by the business tiers, so a
  // personal item says where it is filed rather than the bare 'Sorted' it used to.
  if (status === 'classified') return personalArea ?? (tier === null ? 'Sorted' : tierName(tier));
  if (status === 'failed') return 'Cobrain could not sort this';
  return 'Not sorted yet — yours alone';
}

/**
 * The build the page is running, for the corner of the bar (B65). Short enough to ignore
 * and exact enough to settle an argument: the date and the minute, in the reader's own
 * zone. An unparseable stamp is shown as it stands rather than as a guess.
 */
export function buildStamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return dayMonthTime(new Date(t));
}
